import { composeRefs } from '@radix-ui/react-compose-refs';
import { Slot } from '@radix-ui/react-slot';
import { ArrowUpToLine } from 'lucide-react';
import * as React from 'react';
import { type DropzoneRootProps } from 'react-dropzone';

import { cn } from '@heidi/utils';

import { useDesignSystemIntl } from '../hooks/use-design-system-intl';

import {
  fileDropzoneDescriptionStyles,
  fileDropzoneIconStyles,
  fileDropzoneTitleStyles,
  fileDropzoneVariants,
} from './file-upload.styles';
import {
  type FileDropzoneDescriptionProps,
  type FileDropzoneIconProps,
  type FileDropzoneProps,
  type FileDropzoneTitleProps,
} from './file-upload.types';
import { useDocumentFilePaste } from './use-document-file-paste';
import {
  fileUploadMethodFromDropEvent,
  useFileUploadDropzone,
} from './use-file-upload-dropzone';
import { validatePastedFiles } from './validate-pasted-files';

const withPartClassName = (
  child: React.ReactElement<{ className?: string }>,
  partStyles: string,
  className?: string
) =>
  React.cloneElement(child, {
    className: cn(partStyles, child.props.className, className),
  });

const FileDropzone = React.forwardRef<HTMLDivElement, FileDropzoneProps>(
  (
    {
      'data-testid': testId,
      className,
      children,
      onFilesAccepted,
      onFilesRejected,
      accept,
      multiple = false,
      maxFiles = 0,
      maxSize,
      minSize,
      disabled = false,
      enablePaste = false,
      pasteScope = 'element',
      inputLabel,
      variant,
      ...props
    },
    ref
  ) => {
    const intl = useDesignSystemIntl();
    const rootElementRef = React.useRef<HTMLDivElement>(null);
    const resolvedInputLabel =
      inputLabel ??
      intl.formatMessage({
        defaultMessage: 'Upload file',
        id: '0zMS4MPykD',
        description: 'Default accessible name for the hidden file-picker input',
      });

    const { getRootProps, getInputProps, isDragActive, normalizedAccept } =
      useFileUploadDropzone({
        accept,
        multiple,
        maxFiles,
        maxSize,
        minSize,
        disabled,
        onFilesAccepted,
        onFilesRejected,
        method: fileUploadMethodFromDropEvent,
      });

    const handlePaste = React.useCallback(
      (event: React.ClipboardEvent<HTMLDivElement> | ClipboardEvent) => {
        if (disabled || event.defaultPrevented) return;
        const pastedFiles = Array.from(event.clipboardData?.files ?? []);
        if (pastedFiles.length === 0) return;
        event.preventDefault();
        const { accepted, rejected } = validatePastedFiles(pastedFiles, {
          accept: normalizedAccept,
          multiple,
          maxFiles,
          maxSize,
          minSize,
        });
        if (accepted.length > 0) onFilesAccepted?.(accepted, { method: 'paste' });
        if (rejected.length > 0) onFilesRejected?.(rejected);
      },
      [
        disabled,
        normalizedAccept,
        multiple,
        maxFiles,
        maxSize,
        minSize,
        onFilesAccepted,
        onFilesRejected,
      ]
    );

    useDocumentFilePaste({
      enabled: enablePaste && pasteScope === 'document' && !disabled,
      accept,
      multiple,
      maxFiles,
      maxSize,
      minSize,
      onFilesAccepted,
      onFilesRejected,
      // Editable targets inside the dropzone still route to the file handler.
      containerRef: rootElementRef,
    });

    // Hand off the consumer's handlers to react-dropzone so it composes them
    // with its own (using composeEventHandlers internally). Without this, our
    // {...rootProps} spread would silently clobber any handler the consumer
    // passed via props.
    const {
      onClick: consumerOnClick,
      onKeyDown: consumerOnKeyDown,
      onFocus: consumerOnFocus,
      onBlur: consumerOnBlur,
      onPaste: consumerOnPaste,
      ...restProps
    } = props;
    const { ref: dropzoneRef, ...rootProps } = getRootProps<DropzoneRootProps>({
      onClick: consumerOnClick,
      onKeyDown: consumerOnKeyDown,
      onFocus: consumerOnFocus,
      onBlur: consumerOnBlur,
    });

    return (
      <div
        {...restProps}
        {...rootProps}
        ref={composeRefs(ref, rootElementRef, dropzoneRef as React.Ref<HTMLDivElement>)}
        data-testid={testId}
        onPaste={
          enablePaste && pasteScope === 'element'
            ? (event) => {
                consumerOnPaste?.(event);
                if (!event.defaultPrevented) handlePaste(event);
              }
            : consumerOnPaste
        }
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- paste target: the dropzone must be focusable to receive clipboard paste events; the (labelled) hidden file input remains the interactive control
        tabIndex={enablePaste ? 0 : rootProps.tabIndex}
        className={cn(
          fileDropzoneVariants({ variant, isDragActive, isDisabled: disabled }),
          className
        )}
      >
        <input
          {...getInputProps({ 'aria-label': resolvedInputLabel })}
          data-testid={testId ? `${testId}-input` : undefined}
        />
        {children}
      </div>
    );
  }
);
FileDropzone.displayName = 'FileDropzone';

const FileDropzoneTitle = React.forwardRef<HTMLParagraphElement, FileDropzoneTitleProps>(
  ({ children, className, asChild = false, ...props }, ref) => {
    if (asChild && React.isValidElement<{ className?: string }>(children)) {
      return (
        <Slot ref={ref} {...props}>
          {withPartClassName(children, fileDropzoneTitleStyles, className)}
        </Slot>
      );
    }

    return (
      <p ref={ref} className={cn(fileDropzoneTitleStyles, className)} {...props}>
        {children}
      </p>
    );
  }
);
FileDropzoneTitle.displayName = 'FileDropzoneTitle';

const FileDropzoneDescription = React.forwardRef<
  HTMLParagraphElement,
  FileDropzoneDescriptionProps
>(({ children, className, asChild = false, ...props }, ref) => {
  if (asChild && React.isValidElement<{ className?: string }>(children)) {
    return (
      <Slot ref={ref} {...props}>
        {withPartClassName(children, fileDropzoneDescriptionStyles, className)}
      </Slot>
    );
  }

  return (
    <p ref={ref} className={cn(fileDropzoneDescriptionStyles, className)} {...props}>
      {children}
    </p>
  );
});
FileDropzoneDescription.displayName = 'FileDropzoneDescription';

const FileDropzoneIcon = React.forwardRef<SVGSVGElement, FileDropzoneIconProps>(
  ({ children, className, 'aria-hidden': ariaHidden = true, ...props }, ref) => {
    const icon: React.ReactElement<{ className?: string }> = children ?? (
      <ArrowUpToLine />
    );

    return (
      <Slot
        ref={ref as React.Ref<HTMLElement>}
        aria-hidden={ariaHidden}
        {...(props as React.ComponentProps<typeof Slot>)}
      >
        {withPartClassName(icon, fileDropzoneIconStyles, className)}
      </Slot>
    );
  }
);
FileDropzoneIcon.displayName = 'FileDropzoneIcon';

export { FileDropzone, FileDropzoneDescription, FileDropzoneIcon, FileDropzoneTitle };
